import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ExternalLink, Mail, Phone } from 'lucide-react'
import { platformAPI } from '../../services/api'
import { t, formatDate, formatDateTime } from '../../i18n'
import BillingTab from '../owner/BillingTab'
import { FEATURE_INFO } from '../../config/features'
import { SERVICES } from '../../config/services'
import { PageHeader, SearchInput, Select, Table, Loading, Empty, Drawer, Chip, SubscriptionChip, TypeChip, StatCard, money, cents, roleLabel } from './ui'

const storeLink = b => b.website_url || `/?branch=${b.id}`

// The services this business runs (no plan limit for the platform owner).
function ServicesSection({ business, onChanged }) {
  const [chosen, setChosen] = useState(business.services || [])
  const [saving, setSaving] = useState(false)
  useEffect(() => setChosen(business.services || []), [business.services])
  const changed = chosen.join() !== (business.services || []).join()
  const toggle = id => setChosen(list => list.includes(id) ? (list.length > 1 ? list.filter(s => s !== id) : list) : [...list, id])
  const save = async () => {
    setSaving(true)
    try {
      await platformAPI.setServices(business.id, chosen)
      toast.success(t('Services updated'))
      onChanged?.()
    } catch (e) {
      toast.error(t(e.response?.data?.error || 'Failed to update the business'))
    } finally { setSaving(false) }
  }
  return (
    <section className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <h3 className="text-sm font-semibold text-slate-900">{t('Services')}</h3>
      <p className="mt-0.5 text-xs text-slate-500">{t('What this business offers on its website and admin. The first one opens as its home page.')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {SERVICES.map(s => (
          <button key={s.id} type="button" onClick={() => toggle(s.id)} aria-pressed={chosen.includes(s.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ring-1 ${chosen.includes(s.id) ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50'}`}>
            {t(s.title)}
          </button>
        ))}
      </div>
      {changed && <button type="button" onClick={save} disabled={saving} className="mt-3 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">{t('Save services')}</button>}
    </section>
  )
}

// Turn sections of this business's admin panel on or off. Saved on each switch.
function FeaturesSection({ businessId, features }) {
  const [disabled, setDisabled] = useState(features?.disabled || [])
  const [saving, setSaving] = useState(null)
  useEffect(() => setDisabled(features?.disabled || []), [features])
  if (!features) return null

  const toggle = async key => {
    const next = disabled.includes(key) ? disabled.filter(k => k !== key) : [...disabled, key]
    const previous = disabled
    setDisabled(next)
    setSaving(key)
    try {
      await platformAPI.setFeatures(businessId, next)
      toast.success(next.includes(key) ? t('{v0} hidden for this business', { v0: t(FEATURE_INFO[key].name) }) : t('{v0} turned on for this business', { v0: t(FEATURE_INFO[key].name) }))
    } catch (e) {
      setDisabled(previous)
      toast.error(t(e.response?.data?.error || 'Failed to update the business'))
    } finally { setSaving(null) }
  }

  return (
    <section className="rounded-xl bg-white ring-1 ring-slate-200">
      <div className="border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">{t('Features')}</h3>
        <p className="mt-0.5 text-xs text-slate-500">{t('Choose what this business sees in its admin panel. Hidden sections disappear from its menu and stop working.')}</p>
      </div>
      <ul className="divide-y divide-slate-100">
        {features.available.map(key => {
          const on = !disabled.includes(key)
          const info = FEATURE_INFO[key] || { name: key, description: '' }
          return (
            <li key={key} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${on ? 'text-slate-900' : 'text-slate-400'}`}>{t(info.name)}</p>
                <p className="text-xs text-slate-500">{t(info.description)}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={t(info.name)}
                disabled={saving === key}
                onClick={() => toggle(key)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${on ? 'bg-indigo-600' : 'bg-slate-300'}`}
              >
                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0.5 rtl:-translate-x-0.5'}`} />
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default function PlatformBusinesses() {
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState(null)
  const [search, setSearch] = useState('')
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  const openId = params.get('open')

  const load = useCallback(() => {
    platformAPI.getBusinesses({ search: search || undefined, type: type || undefined, status: status || undefined })
      .then(r => setRows(r.data.businesses))
      .catch(e => { setRows([]); toast.error(t(e.response?.data?.error || 'Failed to load businesses')) })
  }, [search, type, status])

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id) }, [load])

  const open = id => setParams(id ? { open: String(id) } : {})

  return (
    <div>
      <PageHeader title={t('Businesses')} subtitle={t('Every store on the platform, its owner and its subscription.')}>
        <div className="flex flex-wrap gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder={t('Search name, owner or email')} />
          <Select value={type} onChange={setType}>
            <option value="">{t('All types')}</option>
            <option value="restaurant">{t('Restaurant')}</option>
            <option value="ecommerce">{t('Online store')}</option>
            <option value="appointments">{t('Appointments')}</option>
            <option value="rooms">{t('Rooms')}</option>
          </Select>
          <Select value={status} onChange={setStatus}>
            <option value="">{t('All statuses')}</option>
            <option value="active">{t('Active')}</option>
            <option value="suspended">{t('Suspended')}</option>
            <option value="trialing">{t('Trial')}</option>
            <option value="past_due">{t('Past due')}</option>
            <option value="none">{t('No subscription')}</option>
          </Select>
        </div>
      </PageHeader>

      {!rows ? <Loading /> : !rows.length ? <Empty>{t('No businesses match.')}</Empty> : (
        <Table head={[t('Business'), t('Owner'), t('Type'), t('Subscription'), t('Orders (30 days)'), t('Sales (30 days)'), t('Joined')]}>
          {rows.map(b => (
            <tr key={b.id} onClick={() => open(b.id)} className="cursor-pointer hover:bg-slate-50">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {b.logo_url ? <img src={b.logo_url} alt="" className="h-8 w-8 rounded-md object-cover" /> : <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-xs font-semibold text-slate-500">{b.name.slice(0, 2).toUpperCase()}</div>}
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900">{b.name}</p>
                    <p className="truncate text-xs text-slate-500">{b.slug}{!b.is_active && <span className="ms-2"><Chip tone="red">{t('Suspended')}</Chip></span>}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <p className="text-slate-900">{b.owner?.full_name || b.owner?.username || '—'}</p>
                <p className="text-xs text-slate-500">{b.owner?.email || b.owner?.username || ''}</p>
              </td>
              <td className="px-4 py-3"><TypeChip type={b.business_type} /></td>
              <td className="px-4 py-3">
                <SubscriptionChip status={b.subscription_status} />
                {b.price && <p className="mt-1 text-xs text-slate-500">{cents(b.price.cents, b.price.currency)}{t('/mo')}</p>}
              </td>
              <td className="px-4 py-3 tabular-nums">{b.orders_30d}</td>
              <td className="px-4 py-3 tabular-nums">{money(b.revenue_30d, b.currency)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(b.created_at)}</td>
            </tr>
          ))}
        </Table>
      )}

      <BusinessDrawer id={openId} onClose={() => open(null)} onChanged={load} />
    </div>
  )
}

function BusinessDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  const load = useCallback(() => {
    if (!id) return
    setData(null)
    platformAPI.getBusiness(id).then(r => setData(r.data)).catch(e => { toast.error(t(e.response?.data?.error || 'Failed to load the business')); closeRef.current() })
  }, [id])
  useEffect(load, [load])

  const toggleActive = async () => {
    const b = data.business
    if (b.is_active && !window.confirm(t('Suspend {v0}? Its website, ordering and admin panel stop working until you reactivate it.', { v0: b.name }))) return
    setBusy(true)
    try {
      await platformAPI.setBusinessActive(b.id, !b.is_active)
      toast.success(b.is_active ? t('Business suspended') : t('Business reactivated'))
      load(); onChanged()
    } catch (e) {
      toast.error(t(e.response?.data?.error || 'Failed to update the business'))
    } finally { setBusy(false) }
  }

  const b = data?.business
  return (
    <Drawer open={!!id} onClose={onClose} title={b?.name || t('Business')}>
      {!data ? <Loading /> : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <TypeChip type={b.business_type} />
            <SubscriptionChip status={b.subscription_status} />
            {b.is_active ? <Chip tone="green">{t('Live')}</Chip> : <Chip tone="red">{t('Suspended')}</Chip>}
            <a href={storeLink(b)} target="_blank" rel="noreferrer" className="ms-auto inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700">
              {t('Open website')}<ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatCard label={t('Orders (30 days)')} value={b.orders_30d} />
            <StatCard label={t('Sales (30 days)')} value={money(b.revenue_30d, b.currency)} />
            <StatCard label={t('Sales, all time')} value={money(b.revenue_total, b.currency)} hint={t('{v0} paid orders', { v0: b.paid_orders_total })} />
            <StatCard label={t('Customers')} value={b.customer_count} hint={t('{v0} staff', { v0: b.staff_count })} />
          </div>

          <section className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">{t('Owner')}</h3>
            {b.owner ? (
              <div className="mt-2 space-y-1 text-sm text-slate-700">
                <p className="font-medium">{b.owner.full_name || b.owner.username} <span className="text-slate-500">@{b.owner.username}</span></p>
                {b.owner.email && <a href={`mailto:${b.owner.email}`} className="flex items-center gap-2 text-indigo-600"><Mail className="h-3.5 w-3.5" />{b.owner.email}</a>}
                {b.owner.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{b.owner.phone}</p>}
                <p className="text-xs text-slate-500">{t('Last sign-in')}: {b.owner.last_login_at ? formatDateTime(b.owner.last_login_at) : '—'}</p>
              </div>
            ) : <p className="mt-2 text-sm text-slate-500">{t('No owner')}</p>}
            <p className="mt-3 text-xs text-slate-500">{t('Joined')}: {formatDate(b.created_at)}</p>
          </section>

          <ServicesSection business={b} onChanged={() => { platformAPI.getBusiness(b.id).then(r => setData(r.data)); onChanged?.() }} />
          <FeaturesSection businessId={b.id} features={data.features} />

          <section className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">{t('Subscription')}</h3>
            <BillingTab restaurantId={b.id} />
          </section>

          <section className="rounded-xl bg-white ring-1 ring-slate-200">
            <h3 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">{t('Recent orders')}</h3>
            {data.recentOrders.length ? (
              <ul className="divide-y divide-slate-100 text-sm">
                {data.recentOrders.map(o => (
                  <li key={o.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-slate-900">{o.order_code || `#${o.id}`} · {o.customer_name || '—'}</p>
                      <p className="text-xs text-slate-500">{formatDateTime(o.created_at)}</p>
                    </div>
                    <div className="text-end">
                      <p className="tabular-nums text-slate-900">{money(o.total, b.currency)}</p>
                      <p className="text-xs text-slate-500">{t(o.payment_status || '')}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : <p className="px-4 py-6 text-center text-sm text-slate-500">{t('No orders yet')}</p>}
          </section>

          <section className="rounded-xl bg-white ring-1 ring-slate-200">
            <h3 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-900">{t('Staff')}</h3>
            {data.staff.length ? (
              <ul className="divide-y divide-slate-100 text-sm">
                {data.staff.map(s => (
                  <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="truncate text-slate-900">{s.full_name || s.username} <span className="text-slate-500">@{s.username}</span></span>
                    <span className="flex items-center gap-2"><Chip>{roleLabel(s.role)}</Chip>{!s.is_active && <Chip tone="red">{t('Disabled')}</Chip>}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="px-4 py-6 text-center text-sm text-slate-500">{t('No staff accounts')}</p>}
          </section>

          <section className="rounded-xl bg-white p-4 ring-1 ring-rose-200">
            <h3 className="text-sm font-semibold text-slate-900">{b.is_active ? t('Suspend this business') : t('Reactivate this business')}</h3>
            <p className="mt-1 text-sm text-slate-500">{b.is_active ? t('Its website, ordering and admin panel stop working. Nothing is deleted.') : t('Its website, ordering and admin panel work again.')}</p>
            <button disabled={busy} onClick={toggleActive}
              className={`mt-3 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${b.is_active ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {b.is_active ? t('Suspend') : t('Reactivate')}
            </button>
          </section>
        </div>
      )}
    </Drawer>
  )
}
